<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useMilestoneUnlock } from '@/composables/useMilestoneUnlock'
import { MILESTONE_UNLOCK_ROUTES } from '@/types/milestones'
import type { MilestoneKey } from '@/types/milestones'
import Button from '@/components/ui/Button.vue'

const { t } = useI18n()
const router = useRouter()
const { activeUnlockKey, dismissActiveUnlock } = useMilestoneUnlock()

const unlockKey = computed(() => activeUnlockKey.value)

const title = computed(() => {
  if (!unlockKey.value) return ''
  return t(`milestones.unlock.${unlockKey.value}.title`)
})

const body = computed(() => {
  if (!unlockKey.value) return ''
  return t(`milestones.unlock.${unlockKey.value}.body`)
})

function goToFeature() {
  const key = unlockKey.value as MilestoneKey | null
  if (!key) return
  const route = MILESTONE_UNLOCK_ROUTES[key]
  void dismissActiveUnlock()
  if (route) void router.push(route)
}

async function dismiss() {
  await dismissActiveUnlock()
}
</script>

<template>
  <div
    v-if="unlockKey"
    class="fixed inset-x-0 bottom-0 z-[60] p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    role="status"
    aria-live="polite"
  >
    <div
      class="card-panel mx-auto max-w-lg border border-primary-300/60 dark:border-primary-700/50"
    >
      <div class="flex gap-3">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300"
          aria-hidden="true"
        >
          +
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {{ title }}
          </p>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {{ body }}
          </p>
          <div class="mt-3 flex flex-wrap gap-2">
            <Button
              class="min-h-[44px]"
              @click="goToFeature"
            >
              {{ t('milestones.unlockCta') }}
            </Button>
            <Button
              variant="ghost"
              class="min-h-[44px]"
              @click="dismiss"
            >
              {{ t('milestones.dismiss') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
